import { useQuery } from "@tanstack/react-query";
import { Trash2, Copy } from "lucide-react";
import { fetchHospitals } from "../../services/hospitals";
import { useTranslation } from "../../i18n";
import { Field } from "./ui";
import {
  COLOR_OPTIONS,
  dimsForSource,
  METRICS_BY_SOURCE,
  PALETTE,
  SOURCE_LABELS,
  type SourceKey,
  type WidgetConfig as WConfig,
  type WidgetType,
} from "./types";

const SOURCES: SourceKey[] = ["scans", "hospitals", "checkpoints", "devices", "guards"];

// Which config controls apply to which widget types.
const HAS_GROUPING: WidgetType[] = ["bar", "line", "pie", "table", "day_of_week", "comparison", "status_table"];
const HAS_DATA: WidgetType[] = ["metric", ...HAS_GROUPING];
// Sources that expose a floor column (matches data.ts hasFloor).
const FLOOR_SOURCES: SourceKey[] = ["scans", "checkpoints"];

export function WidgetConfigPanel({
  config,
  onChange,
  onDelete,
  onDuplicate,
}: {
  config: WConfig;
  onChange: (patch: Partial<WConfig>) => void;
  onDelete: () => void;
  onDuplicate: () => void;
}) {
  const { t } = useTranslation();
  const { data: hospitals } = useQuery({
    queryKey: ["report-hospitals"],
    queryFn: () => fetchHospitals(),
    staleTime: 5 * 60_000,
  });

  const metrics = METRICS_BY_SOURCE[config.source] || [];
  const dims = dimsForSource(config.source);

  function changeSource(source: SourceKey) {
    const m = METRICS_BY_SOURCE[source];
    const d = dimsForSource(source);
    const metric = m.some((x) => x.key === config.metric) ? config.metric : m[0].key;
    const groupBy = config.groupBy && d.some((x) => x.key === config.groupBy) ? config.groupBy : d[0]?.key ?? null;
    onChange({ source, metric, groupBy });
  }

  const showData = HAS_DATA.includes(config.type);
  const showGrouping = HAS_GROUPING.includes(config.type);
  const inputCls =
    "w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500";

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold uppercase tracking-wide text-gray-500">{t("reportBuilder.configure", "Configure")}</h3>
        <div className="flex gap-1">
          <button onClick={onDuplicate} title={t("reportBuilder.duplicate", "Duplicate")} className="rounded-md p-1.5 text-gray-500 hover:bg-gray-50 hover:text-gray-800">
            <Copy size={16} />
          </button>
          <button onClick={onDelete} title={t("reportBuilder.remove", "Remove")} className="rounded-md p-1.5 text-red-500 hover:bg-red-50">
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      <Field label={t("reportBuilder.fieldTitle", "Title")}>
        <input className={inputCls} value={config.title} onChange={(e) => onChange({ title: e.target.value })} />
      </Field>

      {showData && (
        <>
          <Field label={t("reportBuilder.dataSource", "Data source")}>
            <select className={inputCls} value={config.source} onChange={(e) => changeSource(e.target.value as SourceKey)}>
              {SOURCES.map((s) => (
                <option key={s} value={s}>
                  {SOURCE_LABELS[s]}
                </option>
              ))}
            </select>
          </Field>

          <Field label={t("reportBuilder.metric", "Metric")}>
            <select className={inputCls} value={config.metric} onChange={(e) => onChange({ metric: e.target.value })}>
              {metrics.map((m) => (
                <option key={m.key} value={m.key}>
                  {m.label}
                </option>
              ))}
            </select>
          </Field>
        </>
      )}

      {showGrouping && (
        <Field label={t("reportBuilder.groupBy", "Group by")}>
          <select className={inputCls} value={config.groupBy ?? ""} onChange={(e) => onChange({ groupBy: e.target.value || null })}>
            {config.type !== "day_of_week" &&
              dims.map((d) => (
                <option key={d.key} value={d.key}>
                  {d.label}
                </option>
              ))}
            {config.type === "day_of_week" && <option value="day_of_week">{t("reportBuilder.dayOfWeekOpt", "Day of week")}</option>}
          </select>
        </Field>
      )}

      {config.type === "comparison" && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">{t("reportBuilder.compareAgainst", "Compare against")}</p>
          <Field label={t("reportBuilder.secondSeriesSource", "Second series source")}>
            <select
              className={inputCls}
              value={config.seriesB?.source ?? config.source}
              onChange={(e) => {
                const src = e.target.value as SourceKey;
                onChange({ seriesB: { label: config.seriesB?.label ?? t("reportBuilder.seriesB", "Series B"), source: src, metric: METRICS_BY_SOURCE[src][0].key } });
              }}
            >
              {SOURCES.map((s) => (
                <option key={s} value={s}>
                  {SOURCE_LABELS[s]}
                </option>
              ))}
            </select>
          </Field>
          <div className="mt-2">
            <Field label={t("reportBuilder.secondSeriesMetric", "Second series metric")}>
              <select
                className={inputCls}
                value={config.seriesB?.metric ?? ""}
                onChange={(e) => onChange({ seriesB: { ...(config.seriesB ?? { label: "Series B", source: config.source, metric: "count" }), metric: e.target.value } })}
              >
                {(METRICS_BY_SOURCE[config.seriesB?.source ?? config.source] || []).map((m) => (
                  <option key={m.key} value={m.key}>
                    {m.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div className="mt-2">
            <Field label={t("reportBuilder.secondSeriesLabel", "Second series label")}>
              <input
                className={inputCls}
                value={config.seriesB?.label ?? ""}
                onChange={(e) => onChange({ seriesB: { ...(config.seriesB ?? { source: config.source, metric: "count" }), label: e.target.value } as WConfig["seriesB"] })}
              />
            </Field>
          </div>
        </div>
      )}

      {showData && (
        <>
          <Field label={t("reportBuilder.filterHospital", "Filter: hospital")}>
            <select
              className={inputCls}
              value={config.filters.hospital_id ?? ""}
              onChange={(e) => onChange({ filters: { ...config.filters, hospital_id: e.target.value || undefined } })}
            >
              <option value="">{t("reportBuilder.allHospitals", "All hospitals")}</option>
              {hospitals?.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.name}
                </option>
              ))}
            </select>
          </Field>

          {FLOOR_SOURCES.includes(config.source) && (
            <Field label={t("reportBuilder.filterFloor", "Filter: floor")}>
              <input
                className={inputCls}
                placeholder={t("reportBuilder.floorPlaceholder", "e.g. L1, 5, Plaza")}
                value={config.filters.floor ?? ""}
                onChange={(e) => onChange({ filters: { ...config.filters, floor: e.target.value || undefined } })}
              />
            </Field>
          )}
        </>
      )}

      {showGrouping && (
        <Field label={t("reportBuilder.sort", "Sort")}>
          <select className={inputCls} value={config.sort} onChange={(e) => onChange({ sort: e.target.value as WConfig["sort"] })}>
            <option value="value_desc">{t("reportBuilder.sortValueDesc", "Value, high → low")}</option>
            <option value="value_asc">{t("reportBuilder.sortValueAsc", "Value, low → high")}</option>
            <option value="label_asc">{t("reportBuilder.sortLabelAsc", "Name, A → Z")}</option>
            <option value="label_desc">{t("reportBuilder.sortLabelDesc", "Name, Z → A")}</option>
          </select>
        </Field>
      )}

      {(config.type === "bar" || config.type === "table" || config.type === "day_of_week") && (
        <Field label={t("reportBuilder.maxRows", "Max rows")}>
          <input
            type="number"
            min={1}
            max={100}
            className={inputCls}
            value={config.limit}
            onChange={(e) => onChange({ limit: Math.max(1, Math.min(100, Number(e.target.value) || 10)) })}
          />
        </Field>
      )}

      {config.type === "bar" && (
        <Field label={t("reportBuilder.orientation", "Orientation")}>
          <div className="flex gap-2">
            {(["vertical", "horizontal"] as const).map((o) => (
              <button
                key={o}
                onClick={() => onChange({ orientation: o })}
                className={`flex-1 rounded-lg border px-3 py-1.5 text-sm capitalize ${
                  config.orientation === o ? "border-blue-600 bg-blue-50 font-semibold text-blue-900" : "border-gray-200 text-gray-600"
                }`}
              >
                {o === "vertical" ? t("reportBuilder.vertical", "vertical") : t("reportBuilder.horizontal", "horizontal")}
              </button>
            ))}
          </div>
        </Field>
      )}

      {config.type === "pie" && (
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={config.donut} onChange={(e) => onChange({ donut: e.target.checked })} />
          {t("reportBuilder.donutStyle", "Donut style")}
        </label>
      )}

      {(config.type === "bar" || config.type === "line" || config.type === "pie" || config.type === "day_of_week" || config.type === "status_table" || config.type === "metric") && (
        <Field label={t("reportBuilder.color", "Color")}>
          <div className="flex flex-wrap gap-2">
            {COLOR_OPTIONS.map((c) => (
              <button
                key={c.key}
                title={c.label}
                onClick={() => onChange({ color: c.key })}
                className={`h-7 w-7 rounded-full border-2 ${config.color === c.key ? "border-gray-900" : "border-gray-100"}`}
                style={{ backgroundColor: PALETTE[c.key] }}
              />
            ))}
          </div>
        </Field>
      )}

      <Field label={t("reportBuilder.width", "Width")}>
        <div className="flex gap-2">
          {(["small", "medium", "large"] as const).map((s) => (
            <button
              key={s}
              onClick={() => onChange({ size: s })}
              className={`flex-1 rounded-lg border px-2 py-1.5 text-xs capitalize ${
                config.size === s ? "border-blue-600 bg-blue-50 font-semibold text-blue-900" : "border-gray-200 text-gray-600"
              }`}
            >
              {s === "small" ? "⅓" : s === "medium" ? "½" : t("reportBuilder.widthFull", "Full")}
            </button>
          ))}
        </div>
      </Field>
    </div>
  );
}
